import { AccessComponentEnum, AccessLevelEnum } from 'graphql-common';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import useAssetWithCountersQueryHook, {
  UseAssetQueryHookResult,
} from 'utils/fetch/hooks/useAssetWithCountersQueryHook';
import { AssetViewMode } from 'routes/Assets/Asset/enums';

interface UseActionsProps {
  id?: string;
  viewMode: AssetViewMode;
}

export interface UseActionsResult {
  assetQueryHook: UseAssetQueryHookResult;
  assetId: string;
}

export default function useActions(props: UseActionsProps): UseActionsResult {
  const { id = '', viewMode } = props;

  const { getUserAccessByComponent, getUserAccessByCompany }: ContextProps =
    useOutletContext();
  // LimitedRead users can't read documentations / interventions — the BE
  // resolvers raise NotAuthorized and break the whole counters query, so
  // these sub-fields must be skipped via `@skip(if: ...)`. Asset documents
  // are still queryable under a free plan (per EDM accessibility scopes),
  // so company-level EDM lock alone does not gate the asset doc counter —
  // only the user-role component-level lock does.
  const isDocumentationLocked =
    getUserAccessByComponent(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.LimitedRead;
  const isInterventionsLocked =
    getUserAccessByCompany(AccessComponentEnum.InterventionsManagement) ===
      AccessLevelEnum.LimitedRead ||
    getUserAccessByComponent(AccessComponentEnum.InterventionsManagement) ===
      AccessLevelEnum.LimitedRead;

  // Asset
  const assetQueryHook = useAssetWithCountersQueryHook({
    id,
    includeFullData:
      viewMode === AssetViewMode.View || viewMode === AssetViewMode.Edit,
    isDocumentationLocked,
    isInterventionsLocked,
  });

  return {
    assetId: id,
    assetQueryHook,
  };
}
